import { eventRef } from './eventRef';
export const PRINT_DONE_EVENT = 'badge-print:done';

// Phones/tablets: Android/iOS print through a system dialog (and print services such as Canon Print
// Service) that closing a popup tab can abort, so they print from the current page instead.
// iPadOS reports itself as a Mac; touch points tell them apart. Every iOS browser uses WebKit.
export const isIOS = () => typeof navigator !== 'undefined'
  && (/iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

export const isAndroid = () => typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

/** Event-level opt-in for the "Share badge" button on this device's platform. */
export const badgeShareAllowed = (event) =>
  (isIOS() && event?.badgeShareIos === true) || (isAndroid() && event?.badgeShareAndroid === true);

export const isMobilePrint = () => typeof navigator !== 'undefined'
  && (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

// Android share sheet can hand a PDF straight to Canon PRINT / Canon Print Service and similar apps.
export const canSharePdf = () => {
  if (!isMobilePrint() || typeof navigator.canShare !== 'function' || typeof File === 'undefined') return false;
  try {
    return navigator.canShare({ files: [new File([''], 'badge.pdf', { type: 'application/pdf' })] });
  } catch (_) {
    return false;
  }
};

const waitForAssets = (doc, timeoutMs = 5000, scope = doc) => {
  const images = [...scope.querySelectorAll('img')].filter((img) => !img.complete);
  const loaded = Promise.all(images.map((img) => new Promise((resolve) => {
    img.addEventListener('load', resolve, { once: true });
    img.addEventListener('error', resolve, { once: true });
  })));
  const fonts = doc.fonts?.ready || Promise.resolve();
  return Promise.race([Promise.all([loaded, fonts]), new Promise((resolve) => setTimeout(resolve, timeoutMs))]);
};

const PRINT_ROOT_ID = 'badge-print-root';

/**
 * Prints the badge from the current page: Chrome on Android prints the whole top-level page even when
 * print() is called on an iframe, so the badge is mounted here and print CSS hides everything else.
 * Returns a handle shaped like a window ({ closed }) that closes once the user is back from the
 * system print dialog (or taps Done).
 */
export function printBadgeInPage({ fieldsHtml, widthIn, heightIn, pages = 1 }) {
  document.getElementById(PRINT_ROOT_ID)?.remove();
  document.getElementById(`${PRINT_ROOT_ID}-style`)?.remove();

  const style = document.createElement('style');
  style.id = `${PRINT_ROOT_ID}-style`;
  style.textContent = `
    #${PRINT_ROOT_ID} { display: none; }
    @media print {
      @page { size: ${widthIn}in ${heightIn}in; margin: 0; }
      html, body { width: ${widthIn}in !important; height: ${heightIn}in !important; margin: 0 !important;
        padding: 0 !important; background: #fff !important; overflow: hidden !important; }
      body > *:not(#${PRINT_ROOT_ID}) { display: none !important; }
      #${PRINT_ROOT_ID} { display: block !important; position: relative; width: ${widthIn}in; height: ${heightIn}in;
        background: #fff; font-family: Arial, sans-serif; overflow: hidden; }
      #${PRINT_ROOT_ID} * { margin: 0; padding: 0; box-sizing: border-box; }
      #${PRINT_ROOT_ID} div { color: #000 !important; }
    }${pages > 1 ? `
    @media print {
      html, body { height: auto !important; overflow: visible !important; }
      #${PRINT_ROOT_ID} { height: auto; overflow: visible; }
      #${PRINT_ROOT_ID} .badge-page { position: relative; width: ${widthIn}in; height: ${heightIn}in; overflow: hidden;
        break-after: page; page-break-after: always; }
      #${PRINT_ROOT_ID} .badge-page:last-child { break-after: auto; page-break-after: auto; }
    }` : ''}`;
  const root = document.createElement('div');
  root.id = PRINT_ROOT_ID;
  root.innerHTML = fieldsHtml;
  document.head.appendChild(style);
  document.body.appendChild(root);

  const handle = { closed: false };
  let leftPage = false;
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') leftPage = true;
    else if (leftPage) finish();
  };
  const onFocus = () => {
    if (leftPage) finish();
  };
  const onBlur = () => { leftPage = true; };
  function finish() {
    if (handle.closed) return;
    handle.closed = true;
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('blur', onBlur);
    window.removeEventListener(PRINT_DONE_EVENT, finish);
    root.remove();
    style.remove();
  }

  waitForAssets(document, 5000, root).then(() => {
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur, { once: true });
    window.addEventListener('focus', onFocus);
    window.addEventListener(PRINT_DONE_EVENT, finish);
    window.print();
  });
  return handle;
}

// Resolves once the badge print window closes (it closes itself after print or cancel).
export function waitForPrintWindow(printWindow, { maxWaitMs = 5 * 60 * 1000 } = {}) {
  return new Promise((resolve) => {
    if (!printWindow) {
      resolve();
      return;
    }
    const startedAt = Date.now();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearInterval(timer);
      window.removeEventListener(PRINT_DONE_EVENT, finish);
      resolve();
    };
    const timer = setInterval(() => {
      if (printWindow.closed || Date.now() - startedAt > maxWaitMs) finish();
    }, 500);
    window.addEventListener(PRINT_DONE_EVENT, finish);
  });
}

export const deskPath = (eventId) => `/desk/${eventRef(eventId)}`;
